import React, {FC} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  buildTeamNamespace,
  findSelectedFilterForTeam,
  isDemoInbox,
} from '../../../../../helpers/modelHelpers';
import {makeMapStateToProps} from '../../../../../helpers/reselectHelpers';
import {InboxModel} from '../../../../../models/api/inboxModel';
import {StartGuideTaskTypesEnum} from '../../../../../models/api/preferencesModel';
import {PermissionsEnum} from '../../../../../models/api/roleModel';
import {InboxesFiltersEnum} from '../../../../../models/url/inboxesUrlModel';
import {AppState} from '../../../../../store/appState';
import {getHasPermission} from '../../../../../store/data/access/accessSelectors';
import {getUserParticipatingChannels} from '../../../../../store/data/channels/channelsSelectors';
import {
  getUserOwnedInboxes,
  getUserTeamParticipatingInboxes,
} from '../../../../../store/data/inboxes/inboxesSelectors';
import {getSelectedFilters} from '../../../../../store/data/localPreferences/localPreferencesSelectors';
import {updateStartGuideTasksAction} from '../../../../../store/data/preferences/preferencesActions';
import {getCompletedStartGuideTasks} from '../../../../../store/data/preferences/preferencesSelectors';
import {getHasTeammates, getIsUserGlobalAdmin} from '../../../../../store/data/teammates/teammatesSelectors';
import {getLastUpdatedTeam} from '../../../../../store/data/teams/teamsSelectors';
import {
  getHasCellsEmptySetupActions,
  getHasNewTeammateInvite,
} from '../../../../../store/featureFlags/featureFlagsSelectors';
import {CellsContext} from '../../../../../store/panels/cells/context/cellsContext';
import {
  hasSearchInInboxesParameters,
  InboxesParameters,
  InboxesParametersTypesEnum,
  InboxesSearchParameters,
} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {CellsPanelId} from '../../../../../store/panels/panelStateBase';
import {navigateToLinkAction} from '../../../../../store/stateless/navigationActions';
import {
  searchByParametersAction,
  SearchInteractionOptions,
} from '../../../../../store/stateless/searchActions';
import {SearchNoResults} from '../../../search/noResults/searchNoResults';
import {CellsEmptySetupActionsVariantsEnum, StyledEmptyDiv} from './cellsEmptyCommon';
import {CellsEmptyMessage} from './cellsEmptyMessage';
import {CellsEmptyTeammateSetupActions} from './cellsEmptyTeammateSetupActions';
import {CellsEmptyTeammatesSetupActions} from './cellsEmptyTeammatesSetupActions';
import {CellsEmptyTeamSetupActions} from './cellsEmptyTeamSetupActions';

/*
 * Props.
 */

interface CellsEmptyContainerProps {
  parameters: InboxesParameters;
  cellsContext: CellsContext;
}

interface CellsEmptyContainerComponentProps {
  panelId: CellsPanelId;
  parameters: InboxesParameters;
  cellsContext: CellsContext;
  shouldShowAddTeamInbox: boolean;
  ctaVariant: CellsEmptySetupActionsVariantsEnum | null;
  teamId?: number;
  selectedFilter?: InboxesFiltersEnum;
  selectedTeamDemoInbox?: InboxModel;
  navigate: (destination: string) => Handler;
  navigateToSearch: (
    searchParameters: InboxesSearchParameters,
    cellsContext?: CellsContext,
    options?: SearchInteractionOptions,
  ) => void;
  dismissCellsEmtpySetupActionsVariant: (task: StartGuideTaskTypesEnum) => Handler;
  hasNewTeammateInvite: boolean;
}

/*
 * Selectors.
 */

function getParameters(_state: AppState, props: CellsEmptyContainerProps) {
  return props.parameters;
}

const getSelectedTeamFilter = createSelector(
  [getLastUpdatedTeam, getSelectedFilters],
  (team, selectedFilters) => {
    if (!team) {
      return undefined;
    }

    return findSelectedFilterForTeam(selectedFilters, team.id);
  },
);

const getSelectedTeamDemoInbox = createSelector(
  [getParameters, getUserTeamParticipatingInboxes],
  (parameters, userTeamParticipatingInboxes) => {
    // If we're not in an inbox workspace, we can't have selected a demo inbox.
    if (parameters.type !== InboxesParametersTypesEnum.INBOX || !parameters.inboxId) {
      return undefined;
    }

    // Find the selected inbox.
    const selectedInbox = userTeamParticipatingInboxes.find((i) => i.id === parameters.inboxId);

    // Confirm that this inbox is a demo inbox.
    if (!selectedInbox || !isDemoInbox(selectedInbox)) {
      return undefined;
    }

    return selectedInbox;
  },
);

function makeGetShouldShowAddTeamInbox() {
  return createSelector(
    [getUserTeamParticipatingInboxes, getHasPermission, getLastUpdatedTeam, getParameters],
    (userTeamParticipatingInboxes, hasPermission, lastUpdatedTeam, parameters) => {
      let namespace: string | undefined;

      // If we are in the user's workspace, team inboxes should be added to the last updated team.
      if (parameters.type === InboxesParametersTypesEnum.USER && lastUpdatedTeam) {
        namespace = lastUpdatedTeam.namespace;
      }
      // If we are in a team workspace, use that team's namespace.
      else if (parameters.type === InboxesParametersTypesEnum.TEAM && parameters.teamId) {
        namespace = buildTeamNamespace(parameters.teamId);
      }

      if (!namespace) {
        return false;
      }

      // If we are in the team workspace, then we do not count the demo inbox as a team inbox,
      // so when users archive the demo inbox the CellsEmptySetupActions will be displayed.
      const hasInbox = Boolean(
        userTeamParticipatingInboxes.find((i) => {
          if (parameters.type === InboxesParametersTypesEnum.TEAM) {
            return i.namespace === namespace && !isDemoInbox(i);
          }

          return i.namespace === namespace;
        }),
      );

      // Show the add team inbox prompt for a team (represented by namespace) if the user has permission to modify the team,
      // and that team has no inboxes accessible to the user.
      return hasPermission(namespace, PermissionsEnum.CREATE_INBOX) && !hasInbox;
    },
  );
}

const getShouldAddChannelToDemoInbox = createSelector(
  [getParameters, getUserTeamParticipatingInboxes, getUserParticipatingChannels, getHasPermission],
  (cellsRouteParameters, userTeamParticipatingInboxes, userParticipatingChannels, hasPermission) => {
    // If we haven't selected an inbox workspace, return.
    if (cellsRouteParameters.type !== InboxesParametersTypesEnum.INBOX || !cellsRouteParameters.inboxId) {
      return false;
    }

    const inbox = userTeamParticipatingInboxes.find((i) => i.id === cellsRouteParameters.inboxId);

    if (!inbox) {
      return false;
    }

    // If we're in a demo inbox workspace, that inbox has no channels, and the user has persmission to add channels,
    // we should prompt the user to do so.
    return (
      isDemoInbox(inbox) &&
      !userParticipatingChannels.find((c) => c.inboxId === inbox.id) &&
      hasPermission(inbox.namespace, PermissionsEnum.CREATE_INBOX)
    );
  },
);

function makeGetSetupActionsVariant() {
  return createSelector(
    [
      getUserOwnedInboxes,
      makeGetShouldShowAddTeamInbox(),
      getShouldAddChannelToDemoInbox,
      getHasTeammates,
      getIsUserGlobalAdmin,
      getCompletedStartGuideTasks,
      getParameters,
      getHasCellsEmptySetupActions,
    ],
    (
      userOwnedInboxes,
      shouldShowAddTeamInbox,
      shouldAddChannelToDemoInbox,
      hasTeammates,
      isUserGlobalAdmin,
      completedStartGuideTasks,
      parameters,
      hasCellsEmptySetupActions,
    ) => {
      if (!hasCellsEmptySetupActions) {
        return null;
      }

      const {type} = parameters;
      const shouldAddIndividualInbox = userOwnedInboxes.every((inbox) => isDemoInbox(inbox));

      // If we are in the teammate workspace, have no user owned inboxes, and
      // have not dismissed the TEAMMATE setup actions, return TEAMMATE variant.
      if (
        (type === InboxesParametersTypesEnum.USER || type === InboxesParametersTypesEnum.TEAMMATE_INBOX) &&
        shouldAddIndividualInbox &&
        !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.CELLS_EMPTY_SETUP_ACTION_TEAMMATE_DISMISS)
      ) {
        return CellsEmptySetupActionsVariantsEnum.TEAMMATE;
      }

      // If we are in the team workspace and should add a team inbox,
      // or are in an demo inbox workspace and should add a channel,
      // and have not dismissed the TEAM setup actions, return TEAM variant.
      if (
        ((type === InboxesParametersTypesEnum.TEAM && shouldShowAddTeamInbox) ||
          (type === InboxesParametersTypesEnum.INBOX && shouldAddChannelToDemoInbox)) &&
        !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.CELLS_EMPTY_SETUP_ACTION_TEAM_DISMISS)
      ) {
        return CellsEmptySetupActionsVariantsEnum.TEAM;
      }

      // If we are in the teammates workspace, have no teammates, are a global
      // admin, and have not dismissed the TEAMMATES setup actions, return TEAMMATES variant.
      if (
        type === InboxesParametersTypesEnum.GLOBAL &&
        !hasTeammates &&
        isUserGlobalAdmin &&
        !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.CELLS_EMPTY_SETUP_ACTION_TEAMMATES_DISMISS)
      ) {
        return CellsEmptySetupActionsVariantsEnum.TEAMMATES;
      }

      return null;
    },
  );
}

/*
 * Component.
 */

const CellsEmptyContainerComponent: FC<CellsEmptyContainerComponentProps> = (props) => {
  const {
    panelId,
    parameters,
    cellsContext,
    shouldShowAddTeamInbox,
    selectedTeamDemoInbox,
    selectedFilter,
    teamId,
    ctaVariant,
    navigate,
    dismissCellsEmtpySetupActionsVariant,
  } = props;
  const {namespace} = cellsContext;

  switch (ctaVariant) {
    case CellsEmptySetupActionsVariantsEnum.TEAMMATE: {
      return (
        <CellsEmptyTeammateSetupActions
          type={ctaVariant}
          navigate={navigate}
          namespace={namespace}
          shouldShowAddTeamInbox={shouldShowAddTeamInbox}
          teamId={teamId}
          selectedFilter={selectedFilter}
          onDismissClick={dismissCellsEmtpySetupActionsVariant(
            StartGuideTaskTypesEnum.CELLS_EMPTY_SETUP_ACTION_TEAMMATE_DISMISS,
          )}
        />
      );
    }
    case CellsEmptySetupActionsVariantsEnum.TEAM: {
      return (
        <CellsEmptyTeamSetupActions
          type={ctaVariant}
          navigate={navigate}
          namespace={namespace}
          onDismissClick={dismissCellsEmtpySetupActionsVariant(
            StartGuideTaskTypesEnum.CELLS_EMPTY_SETUP_ACTION_TEAM_DISMISS,
          )}
          selectedTeamDemoInbox={selectedTeamDemoInbox}
        />
      );
    }
    case CellsEmptySetupActionsVariantsEnum.TEAMMATES: {
      return (
        <CellsEmptyTeammatesSetupActions
          type={ctaVariant}
          navigate={navigate}
          namespace={namespace}
          onDismissClick={dismissCellsEmtpySetupActionsVariant(
            StartGuideTaskTypesEnum.CELLS_EMPTY_SETUP_ACTION_TEAMMATES_DISMISS,
          )}
          hasNewTeammateInvite={props.hasNewTeammateInvite}
        />
      );
    }
    default: {
      // Return early if we don't need to display a CTA to search in other search filters.
      if (!hasSearchInInboxesParameters(parameters)) {
        return <CellsEmptyMessage cellsContext={cellsContext} />;
      }

      return (
        <StyledEmptyDiv>
          <SearchNoResults
            panelId={panelId}
            parameters={parameters}
            cellsContext={cellsContext}
            navigateToSearch={props.navigateToSearch}
          />
        </StyledEmptyDiv>
      );
    }
  }
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CellsEmptyContainerProps>()(() => ({
  ctaVariant: makeGetSetupActionsVariant(),
  shouldShowAddTeamInbox: makeGetShouldShowAddTeamInbox(),
  selectedTeamDemoInbox: getSelectedTeamDemoInbox,
  lastUpdatedTeam: getLastUpdatedTeam,
  selectedFilter: getSelectedTeamFilter,
  hasNewTeammateInvite: getHasNewTeammateInvite,
}));

const mapDispatchToProps = {
  navigateToLinkAction,
  searchByParametersAction,
  updateStartGuideTasksAction,
};

export const CellsEmptyContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const navigate = (destination: string) => () => dispatchProps.navigateToLinkAction(destination);
    const navigateToSearch = (
      searchParameters: InboxesSearchParameters,
      cellsContext?: CellsContext,
      options?: SearchInteractionOptions,
    ) => dispatchProps.searchByParametersAction(searchParameters, cellsContext, options);
    const dismissCellsEmtpySetupActionsVariant = (task: StartGuideTaskTypesEnum) => () =>
      dispatchProps.updateStartGuideTasksAction(task);

    const teamId = stateProps.lastUpdatedTeam && stateProps.lastUpdatedTeam.id;

    return {
      ...stateProps,
      ...ownProps,
      teamId,
      navigate,
      navigateToSearch,
      dismissCellsEmtpySetupActionsVariant,
    };
  },
)(CellsEmptyContainerComponent);
